import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Divider,
  IconButton,
  Link,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { getMatchConfidenceLevel } from '../../utils/directorImportTemplate';

const DuplicateResolutionPanel = ({ open, director, onClose, onResolve }) => {
  const [selectedAction, setSelectedAction] = useState('');

  const handleResolve = () => {
    if (!selectedAction) return;

    const resolution = {
      directorIndex: director._rowIndex,
      action: selectedAction,
      // If mapping to existing, include the director ID
      existingDirectorId: selectedAction.startsWith('map-') 
        ? selectedAction.replace('map-', '') 
        : null,
    };

    onResolve(resolution);
  };

  const renderDirectorInfo = (directorData, title, isImported = false) => (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: isImported ? '#f0f7ff' : '#f5f5f5' }}>
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        {title}
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {directorData.firstName && directorData.lastName && (
          <Box display="flex" alignItems="center" gap={1}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {directorData.firstName} {directorData.lastName}
            </Typography>
          </Box>
        )}
        {directorData.dateOfBirth && (
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarIcon fontSize="small" color="action" />
            <Typography variant="body2">
              DOB: {directorData.dateOfBirth}
            </Typography>
          </Box>
        )}
        {directorData.email && (
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {directorData.email}
            </Typography>
          </Box>
        )}
        {directorData.phone && (
          <Box display="flex" alignItems="center" gap={1}>
            <PhoneIcon fontSize="small" color="action" />
            <Typography variant="body2">
              {directorData.phone}
            </Typography>
          </Box>
        )}
        {directorData.passportNumber && (
          <Box display="flex" alignItems="center" gap={1}>
            <BadgeIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Passport: {directorData.passportNumber}
            </Typography>
          </Box>
        )}
        {directorData.emiratesId && (
          <Box display="flex" alignItems="center" gap={1}>
            <BadgeIcon fontSize="small" color="action" />
            <Typography variant="body2">
              Emirates ID: {directorData.emiratesId}
            </Typography>
          </Box>
        )}
        {directorData.nationality && (
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="body2" color="textSecondary">
              Nationality: {directorData.nationality}
            </Typography>
          </Box>
        )}
        {directorData.companyCount !== undefined && (
          <Box display="flex" alignItems="center" gap={1}>
            <BusinessIcon fontSize="small" color="action" />
            <Typography variant="body2" color="textSecondary">
              {directorData.companyCount} {directorData.companyCount === 1 ? 'company' : 'companies'}
            </Typography>
          </Box>
        )}
      </Box>
    </Paper>
  );

  const renderMatchOption = (match, index) => {
    const confidence = getMatchConfidenceLevel(match.score);
    
    return (
      <Box key={match.directorId} sx={{ mb: 2 }}>
        <FormControlLabel
          value={`map-${match.directorId}`}
          control={<Radio />}
          label={
            <Box>
              <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                <Typography variant="body2" fontWeight={500}>
                  Match #{index + 1}
                </Typography>
                <Chip
                  label={confidence.label}
                  size="small"
                  color={confidence.color}
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
                <Chip
                  label={`${match.score}% match`}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.7rem' }}
                />
              </Box>
            </Box>
          }
          sx={{ alignItems: 'flex-start', mb: 1 }}
        />
        <Box sx={{ ml: 4 }}>
          {renderDirectorInfo(match, '', false)}
          {match.directorId && (
            <Link
              href={`/directors/${match.directorId}`}
              target="_blank"
              variant="caption"
              sx={{ display: 'inline-block', mb: 1 }}
            >
              View Full Profile →
            </Link>
          )}
        </Box>
      </Box>
    );
  };

  if (!director) return null;

  const hasMatches = director.matches && director.matches.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          height: '90vh',
          maxHeight: '900px',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Resolve {director.status === 'duplicate' ? 'Duplicate' : 'Error'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {/* Imported Director Info */}
        {renderDirectorInfo(director, `Imported Director (Row #${director._rowIndex + 1})`, true)}

        {/* Validation Errors */}
        {director.errors && director.errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Validation Errors:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {director.errors.map((error, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{error}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Resolution Options */}
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Resolution Options
        </Typography>

        <RadioGroup value={selectedAction} onChange={(e) => setSelectedAction(e.target.value)}>
          {/* Show matches if any */}
          {hasMatches && (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Found {director.matches.length} potential {director.matches.length === 1 ? 'match' : 'matches'} in the system:
              </Typography>

              {director.matches.map((match, index) => renderMatchOption(match, index))}

              <Divider sx={{ my: 2 }} />
            </>
          )}

          {/* Create as New */}
          <FormControlLabel
            value="create-new"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Create as New Director
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Add this as a new director in the system
                </Typography>
              </Box>
            }
            sx={{ mb: 2 }}
          />

          {/* Skip Entry */}
          <FormControlLabel
            value="skip"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  Skip This Entry
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Do not import this director
                </Typography>
              </Box>
            }
          />
        </RadioGroup>

        {/* Guidance */}
        {hasMatches && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="caption">
              <strong>Tip:</strong> Choose "Map to Existing" if this is the same person to avoid duplicates. 
              Choose "Create as New" if this is a different person.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleResolve}
          disabled={!selectedAction}
        >
          Apply Resolution
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateResolutionPanel;
